import * as THREE from 'three'
import { computeBoundsTree, acceleratedRaycast } from 'three-mesh-bvh'
import useEditorStore from '../../stores/useEditorStore'

// patch Mesh.raycast so raycaster.intersectObject uses the BVH if built
THREE.Mesh.prototype.raycast = acceleratedRaycast

/**
 * Given two world‐space points defining a plane origin, return all face indices
 * on the “positive” side of that plane (dot(normal, point) > 0).
 *
 * @param {THREE.Vector3} pointA - First click point
 * @param {THREE.Vector3} pointB - Second click point
 * @param {{ scene:THREE.Scene, camera:THREE.Camera }} sceneRefs
 * @returns {Set<number>} faceIndexSet
 */
export function computeRegionFromPlane(pointA, pointB, sceneRefs) {
    const { scene, camera, shadedMesh:mesh } = sceneRefs;
    
    // Define the cutting plane using the camera and the two points.
  const cameraPosition = new THREE.Vector3();
  camera.getWorldPosition(cameraPosition);

  const vecCamToA = new THREE.Vector3().subVectors(pointA, cameraPosition);
  const vecCamToB = new THREE.Vector3().subVectors(pointB, cameraPosition);
  const planeNormal = new THREE.Vector3().crossVectors(vecCamToA, vecCamToB).normalize();

  // Ensure the normal points "up" relative to the screen line.
  const ndcA = pointA.clone().project(camera);
  const ndcB = pointB.clone().project(camera);
  const screenLineDir = new THREE.Vector2().subVectors(ndcB, ndcA);
  const screenUpDir = new THREE.Vector2(-screenLineDir.y, screenLineDir.x).normalize();

  const testPoint = new THREE.Vector3().addVectors(pointA, planeNormal);
  const ndcTest = testPoint.clone().project(camera);
  const screenNormalDir = new THREE.Vector2().subVectors(ndcTest, ndcA).normalize();

  if (screenNormalDir.dot(screenUpDir) > 0) {
    planeNormal.negate();
  }

  const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, pointA);

  // --- SOLUTION TO ARTIFACTS ---
  // Define a small epsilon based on the mesh size to create a buffer.
  if (!mesh.geometry.boundingSphere) mesh.geometry.computeBoundingSphere();
  const epsilon = mesh.geometry.boundingSphere.radius * 0.001;
  // -----------------------------

  const geom = mesh.geometry;
  if (!geom.boundsTree) {
    computeBoundsTree.call(geom);
  }

  const pos = geom.attributes.position;
  const faceCount = pos.count / 3;
  const selected = new Set();
  const centroid = new THREE.Vector3();

  for (let fi = 0; fi < faceCount; fi++) {
    const vA = new THREE.Vector3().fromBufferAttribute(pos, fi * 3 + 0);
    const vB = new THREE.Vector3().fromBufferAttribute(pos, fi * 3 + 1);
    const vC = new THREE.Vector3().fromBufferAttribute(pos, fi * 3 + 2);
    
    centroid.copy(vA).add(vB).add(vC).multiplyScalar(1 / 3);
    centroid.applyMatrix4(mesh.matrixWorld);

    // THE FIX: Use the epsilon to catch faces that are very close to the plane.
    if (plane.distanceToPoint(centroid) > -epsilon) {
      selected.add(fi);
    }
  }

  return selected;
}

/**
 * Picks a world-space point for scan-prep scissors (and similar): ray hits the
 * shaded mesh first so points lie on the model. Falls back to a camera-facing
 * plane through a reference point only when there is no mesh hit.
 *
 * @param {MouseEvent} event
 * @param {{ camera:THREE.Camera, renderer:THREE.Renderer, scene?:THREE.Scene, shadedMesh?:THREE.Object3D }} sceneRefs
 * @returns {THREE.Vector3 | null}
 */
export function pickAnywhereOnCanvas(event, sceneRefs) {
  const { camera, renderer, scene, shadedMesh } = sceneRefs || {};
  if (!camera || !renderer?.domElement) return null;

  const rect = renderer.domElement.getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

  const pointer = new THREE.Vector2(x, y);
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(pointer, camera);

  if (shadedMesh) {
    const hits = raycaster.intersectObject(shadedMesh, true);
    if (hits.length > 0 && hits[0].point) {
      return hits[0].point.clone();
    }
  }

  if (!scene) return null;

  const center = new THREE.Vector3();
  if (shadedMesh) {
    shadedMesh.updateMatrixWorld(true);
    new THREE.Box3().setFromObject(shadedMesh).getCenter(center);
  } else {
    const refMesh = scene.children.find((obj) => obj.isMesh);
    if (!refMesh) return null;
    refMesh.getWorldPosition(center);
  }
  const normal = new THREE.Vector3();
  camera.getWorldDirection(normal);
  const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, center);
  const point = new THREE.Vector3();
  const hit = raycaster.ray.intersectPlane(plane, point);
  return hit ?? null;
}
